import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Lightbulb, X, Send, CheckCircle } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import useTranslation from "hooks/useTranslation";
import api from "services/api";
import "./index.scss";

/* ─── Toast ─────────────────────────────────────────────────────────────── */
const Toast = ({ visible, onHide, tr }) => {
	useEffect(() => {
		if (!visible) return;
		const t = setTimeout(onHide, 3500);
		return () => clearTimeout(t);
	}, [visible]);

	return ReactDOM.createPortal(
		<div
			style={{
				position: "fixed",
				top: "24px",
				right: "24px",
				zIndex: 99999,
				display: "flex",
				alignItems: "center",
				gap: "12px",
				background: "#fff",
				border: "1.5px solid #bbf7d0",
				borderRadius: "14px",
				padding: "14px 18px",
				boxShadow: "0 8px 32px rgba(34,197,94,0.18)",
				minWidth: "280px",
				maxWidth: "340px",
				pointerEvents: visible ? "auto" : "none",
				opacity: visible ? 1 : 0,
				transform: visible
					? "translateY(0) scale(1)"
					: "translateY(-16px) scale(0.97)",
				transition: "opacity 0.28s ease, transform 0.28s ease"
			}}
		>
			<div
				style={{
					width: "36px",
					height: "36px",
					borderRadius: "50%",
					background: "linear-gradient(135deg,#dcfce7,#bbf7d0)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0
				}}
			>
				<CheckCircle size={18} color="#16a34a" />
			</div>
			<div style={{ flex: 1 }}>
				<p
					style={{
						margin: 0,
						fontWeight: 600,
						fontSize: "13px",
						color: "#14532d"
					}}
				>
					{tr("Предложение отправлено!")}
				</p>
				<p style={{ margin: "2px 0 0", fontSize: "12px", color: "#6F767E" }}>
					{tr("Мы его рассмотрим")}
				</p>
			</div>
			<button
				onClick={onHide}
				style={{
					background: "none",
					border: "none",
					cursor: "pointer",
					color: "#9ca3af",
					padding: "2px",
					display: "flex",
					lineHeight: 0,
					borderRadius: "6px"
				}}
			>
				<X size={14} />
			</button>
		</div>,
		document.body
	);
};

const BoundedPhoneInput = React.forwardRef(({ onChange, ...props }, ref) => {
	const handleChange = e => {
		const digits = (e.target.value || "").replace(/\D/g, "");
		if (digits.length > 12) return;
		onChange && onChange(e);
	};
	return <input {...props} ref={ref} onChange={handleChange} />;
});
BoundedPhoneInput.displayName = "BoundedPhoneInput";

/* ─── Main component ─────────────────────────────────────────────────────── */
const SuggestionButton = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [phone, setPhone] = useState("");
	const [text, setText] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [loading, setLoading] = useState(false);
	const [toast, setToast] = useState(false);
	const [phoneError, setPhoneError] = useState("");
	const { tr } = useTranslation();

	const validatePhone = (val, checkFormat = true) => {
		const digits = (val || "").replace(/\D/g, "");
		if (!val || digits.length === 0) return "";
		if (digits.length < 7)
			return tr("Номер телефона слишком короткий (мин. 7 цифр)");
		if (checkFormat && !isValidPhoneNumber(val))
			return tr("Номер телефона в неверном формате");
		return "";
	};

	const handlePhoneChange = val => {
		const digits = (val || "").replace(/\D/g, "");
		if (digits.length > 15) return;
		setPhone(val);
		if (phoneError) setPhoneError(validatePhone(val, false));
	};

	const handleSubmit = async e => {
		e.preventDefault();
		const err = validatePhone(phone);
		if (err) {
			setPhoneError(err);
			return;
		}
		if (!(phone || "").trim() && !text.trim()) return;
		setLoading(true);
		try {
			await api.request.post("/customer-offers", {
				phone,
				message: text
			});
			setSubmitted(true);
			setToast(true);
			setPhone("");
			setText("");
			setPhoneError("");
		} catch {
			// silent — keep form open so user can retry
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			setSubmitted(false);
			setPhoneError("");
		}, 350);
	};

	const isDisabled =
		loading || (!(phone || "").trim() && !text.trim()) || !!phoneError;

	return ReactDOM.createPortal(
		<>
			<Toast visible={toast} onHide={() => setToast(false)} tr={tr} />

			<div
				style={{
					position: "fixed",
					bottom: "2rem",
					right: "2rem",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-end",
					gap: "14px"
				}}
			>
				{/* ── Card ── */}
				{isOpen && (
					<div
						style={{
							width: "400px",
							background: "#fff",
							borderRadius: "20px",
							overflow: "hidden",
							boxShadow:
								"0 24px 64px rgba(1,37,99,.16), 0 2px 8px rgba(0,0,0,.06)",
							animation: "sg-up .22s ease-out"
						}}
					>
						{/* Header */}
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								padding: "18px 22px",
								background:
									"linear-gradient(135deg,#012563 0%,#1a56db 55%,#2A85FF 100%)"
							}}
						>
							<div
								style={{ display: "flex", alignItems: "center", gap: "10px" }}
							>
								<div
									style={{
										width: "34px",
										height: "34px",
										borderRadius: "10px",
										background: "rgba(255,255,255,.15)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center"
									}}
								>
									<Lightbulb size={17} color="#fff" />
								</div>
								<div>
									<p
										style={{
											margin: 0,
											color: "#fff",
											fontWeight: 700,
											fontSize: "14px",
											lineHeight: 1.3
										}}
									>
										{tr("Предложения и отзывы")}
									</p>
									<p
										style={{
											margin: 0,
											color: "rgba(255,255,255,.65)",
											fontSize: "11px",
											lineHeight: 1.4
										}}
									>
										{tr("Ваше мнение важно для нас")}
									</p>
								</div>
							</div>
							<button
								className="sg-close"
								onClick={handleClose}
								style={{
									background: "rgba(255,255,255,.1)",
									border: "none",
									cursor: "pointer",
									color: "rgba(255,255,255,.7)",
									padding: "7px",
									borderRadius: "10px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									lineHeight: 0,
									transition: "background .15s, color .15s"
								}}
							>
								<X size={16} />
							</button>
						</div>

						{/* Body */}
						<div style={{ padding: "24px" }}>
							{submitted ? (
								/* ── Success state ── */
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										gap: "16px",
										padding: "16px 0 8px"
									}}
								>
									<div
										style={{
											width: "72px",
											height: "72px",
											borderRadius: "50%",
											background: "linear-gradient(135deg,#dcfce7,#bbf7d0)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											boxShadow: "0 8px 24px rgba(34,197,94,.2)"
										}}
									>
										<CheckCircle size={36} color="#16a34a" />
									</div>
									<div style={{ textAlign: "center" }}>
										<p
											style={{
												margin: "0 0 6px",
												fontWeight: 700,
												fontSize: "16px",
												color: "#1A1D1F"
											}}
										>
											{tr("Спасибо!")}
										</p>
										<p
											style={{
												margin: 0,
												fontSize: "13px",
												color: "#6F767E",
												lineHeight: 1.5
											}}
										>
											{tr("Ваше предложение успешно отправлено.")}
											<br />
											{tr("Мы его рассмотрим.")}
										</p>
									</div>
									<button
										onClick={handleClose}
										style={{
											marginTop: "4px",
											padding: "10px 28px",
											borderRadius: "10px",
											border: "none",
											background: "#f4f6fd",
											color: "#2A85FF",
											fontWeight: 600,
											fontSize: "13px",
											cursor: "pointer"
										}}
									>
										{tr("Закрыть")}
									</button>
								</div>
							) : (
								/* ── Form ── */
								<form
									onSubmit={handleSubmit}
									style={{
										display: "flex",
										flexDirection: "column",
										gap: "16px"
									}}
								>
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: "6px"
										}}
									>
										<label
											style={{
												fontSize: "12px",
												fontWeight: 600,
												color: "#6F767E"
											}}
										>
											{tr("Номер телефона")}
										</label>
										<PhoneInput
											international
											defaultCountry="UZ"
											value={phone}
											onChange={handlePhoneChange}
											onBlur={() => setPhoneError(validatePhone(phone))}
											inputComponent={BoundedPhoneInput}
											className={`sg-phone-input${phoneError ? " sg-phone-error" : ""
												}`}
										/>
										{phoneError && (
											<span
												style={{
													fontSize: "11px",
													color: "#ef4444",
													marginTop: "2px"
												}}
											>
												{phoneError}
											</span>
										)}
									</div>

									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: "6px"
										}}
									>
										<label
											style={{
												fontSize: "12px",
												fontWeight: 600,
												color: "#6F767E"
											}}
										>
											{tr("Ваше предложение")}
										</label>
										<textarea
											value={text}
											onChange={e => setText(e.target.value)}
											placeholder={tr("Напишите ваш отзыв или предложение...")}
											rows={5}
											className="sg-input"
											style={{
												padding: "12px 14px",
												borderRadius: "10px",
												border: "1.5px solid #d0daf0",
												background: "#f7f9ff",
												color: "#1A1D1F",
												fontSize: "14px",
												outline: "none",
												width: "100%",
												boxSizing: "border-box",
												resize: "none",
												fontFamily: "inherit",
												lineHeight: 1.55,
												transition: "border-color .15s, box-shadow .15s"
											}}
										/>
									</div>

									<button
										type="submit"
										disabled={isDisabled}
										className="sg-submit"
										style={{
											height: "46px",
											borderRadius: "11px",
											border: "none",
											color: "#fff",
											fontSize: "14px",
											fontWeight: 600,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: "8px",
											cursor: isDisabled ? "not-allowed" : "pointer",
											background: isDisabled ? "#a8c8fa" : "#2A85FF",
											transition: "background .18s",
											boxShadow: isDisabled
												? "none"
												: "0 4px 14px rgba(42,133,255,.35)"
										}}
									>
										{loading ? (
											<span
												style={{
													width: "18px",
													height: "18px",
													borderRadius: "50%",
													border: "2.5px solid #fff",
													borderTopColor: "transparent",
													animation: "sg-spin .7s linear infinite",
													display: "inline-block"
												}}
											/>
										) : (
											<>
												<Send size={15} />
												{tr("Отправить")}
											</>
										)}
									</button>
								</form>
							)}
						</div>
					</div>
				)}

				{/* ── FAB ── */}
				<button
					className="sg-fab"
					onClick={() => (isOpen ? handleClose() : setIsOpen(true))}
					style={{
						width: "58px",
						height: "58px",
						borderRadius: "50%",
						border: "none",
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: isOpen ? "#146bdf" : "#2A85FF",
						boxShadow: "0 8px 24px rgba(42,133,255,.45)",
						transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
						transition: "background .2s, transform .25s, box-shadow .2s",
						flexShrink: 0
					}}
					title={tr("Предложения и отзывы")}
				>
					{isOpen ? (
						<X size={22} color="#fff" />
					) : (
						<Lightbulb size={22} color="#fff" />
					)}
				</button>
			</div>
		</>,
		document.body
	);
};

export default SuggestionButton;
